import { useState } from 'react';
import { useProductos, useCrearProducto, useActualizarProducto, useEliminarProducto } from '../../hooks/useProductos';
import { Button } from '../../components/shared/Button';
import { Spinner } from '../../components/shared/Spinner';
import { ProductFormModal } from '../../components/admin/ProductFormModal';
import { EditIcon, PlusIcon, TrashIcon } from '../../components/shared/icons';
import { useToast } from '../../components/shared/Toast';
import { NuevoProducto, Producto } from '../../types/catalogo';

export function ProductosAdminPage() {
  const { data: productos, isLoading } = useProductos();
  const crear = useCrearProducto();
  const actualizar = useActualizarProducto();
  const eliminar = useEliminarProducto();
  const { mostrarError, mostrarExito } = useToast();

  const [productoEnEdicion, setProductoEnEdicion] = useState<Producto | 'nuevo' | null>(null);

  const guardar = (datos: NuevoProducto) => {
    const onError = (error: unknown) => mostrarError((error as Error).message);
    if (productoEnEdicion && productoEnEdicion !== 'nuevo') {
      actualizar.mutate(
        { id: productoEnEdicion.id, cambios: datos },
        { onSuccess: () => { mostrarExito('Producto actualizado'); setProductoEnEdicion(null); }, onError },
      );
    } else {
      crear.mutate(datos, {
        onSuccess: () => { mostrarExito('Producto creado'); setProductoEnEdicion(null); },
        onError,
      });
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <div className="flex justify-between items-center">
        <div className="text-[13px] text-admin-ink-muted">{productos?.length ?? 0} productos</div>
        <Button onClick={() => setProductoEnEdicion('nuevo')} data-testid="new-product-button">
          <PlusIcon width={15} height={15} />
          Nuevo producto
        </Button>
      </div>

      {isLoading ? (
        <Spinner />
      ) : (
        <div className="bg-white border border-admin-border rounded-xl overflow-x-auto">
          <table className="w-full min-w-[560px] border-collapse text-[13px]">
            <thead>
              <tr>
                {['Producto', 'Categoría', 'Stock', 'Paquetes', ''].map((h) => (
                  <th key={h} className="text-left text-[11px] font-bold uppercase tracking-wide text-admin-ink-muted px-4 py-2.5 border-b border-admin-border">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(productos ?? []).map((producto) => (
                <tr key={producto.id} data-testid={`product-row-${producto.id}`} className="border-b border-admin-border/60 last:border-0">
                  <td className="px-4 py-3.5 font-semibold">{producto.nombre}</td>
                  <td className="px-4 py-3.5">{producto.categoriaId}</td>
                  <td className={`px-4 py-3.5 font-bold ${producto.stock === 0 ? 'text-danger' : ''}`}>
                    {producto.stock}
                  </td>
                  <td className="px-4 py-3.5">{producto.paquetes.length} paquete(s)</td>
                  <td className="px-4 py-3.5 text-right">
                    <div className="inline-flex gap-3.5">
                      <button onClick={() => setProductoEnEdicion(producto)} aria-label="Editar producto">
                        <EditIcon width={15} height={15} className="text-admin-ink-muted" />
                      </button>
                      <button
                        onClick={() => eliminar.mutate(producto.id, { onError: (e) => mostrarError((e as Error).message) })}
                        aria-label="Eliminar producto"
                      >
                        <TrashIcon width={15} height={15} className="text-danger" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {productoEnEdicion && (
        <ProductFormModal
          producto={productoEnEdicion === 'nuevo' ? undefined : productoEnEdicion}
          onClose={() => setProductoEnEdicion(null)}
          onSave={guardar}
          guardando={crear.isPending || actualizar.isPending}
        />
      )}
    </div>
  );
}
